import '@assets/font/sofa/stylesheet.css';
import '@assets/main.css';
import type { AppProps } from 'next/app';
import localFont from 'next/font/local';
import Script from 'next/script';
import { Hydrate, QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { GoogleOAuthProvider } from '@react-oauth/google';
import Head from 'next/head';
import { useQueryErrorResetBoundary } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import { ReactElement, ReactNode, useState } from 'react';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { ErrorViewGlobal } from 'sharedui/ui/EmptyState/ErrorView';
import { NextPage } from 'next';
import { Toaster } from 'sonner';
import { googleClientId } from '@services/constants';

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};
const caros = localFont({
  src: [
    {
      path: '../assets/font/Caros/CarosSoftBlack.woff',
      weight: '900',
    },
    {
      path: '../assets/font/Caros/CarosSoftHeavy.woff',
      weight: '900',
    },
    {
      path: '../assets/font/Caros/CarosSoftBold.woff',
      weight: '700',
    },

    {
      path: '../assets/font/Caros/CarosSoftHeavy.woff',
      weight: '600',
    },
    {
      path: '../assets/font/Caros/CarosSoftMedium.woff',
      weight: '500',
    },
    {
      path: '../assets/font/Caros/CarosSoft.woff',
      weight: '400',
    },
    {
      path: '../assets/font/Caros/CarosSoftLight.woff',
      weight: '300',
    },
    {
      path: '../assets/font/Caros/CarosSoftThin.woff',
      weight: '200',
    },
  ],
  variable: '--font-caros',
});

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
  const getLayout = Component.getLayout ?? ((page) => page);
  const { reset } = useQueryErrorResetBoundary();
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            cacheTime: 1000 * 60 * 60 * 24, // 24 hours
          },
        },
      })
  );

  let localStoragePersister: any;
  if (typeof window !== 'undefined') {
    // Client-side-only code
    localStoragePersister = createSyncStoragePersister({
      storage: window.localStorage,
    });
  }

  return (
    <>
      <Toaster richColors />
      <main className={` ${caros.variable} `}>
        <Script id="stripe-js" src="/js/freshchat.js" />

        <ErrorBoundary onReset={reset} fallbackRender={ErrorViewGlobal}>
          <ToastContainer />
          {/* <NextNProgress showOnShallow={false} color="#F1592D" /> */}
          <Head>
            <meta
              name="viewport"
              content="initial-scale=1.0, width=device-width"
              key="title"
            />
            <link rel="icon" href="/assets/icons/fryt-logo.svg" />
          </Head>
          <div className=" bg-[#F8FAFC] min-h-screen   ">
            <PersistQueryClientProvider
              client={queryClient}
              persistOptions={{
                persister: localStoragePersister,
                buster: 'frythubb',
              }}
            >
              <Hydrate state={pageProps.dehydratedState}>
                <GoogleOAuthProvider
                  key="AIzaSyBOKX2OUzW39UfpTvLqvC3rrzhbE2797J4"
                  clientId={
                    googleClientId ||
                    '368332247124-lmpdrhbdrapu2mafenjam88t8s88k3ol.apps.googleusercontent.com'
                  }
                >
                  {getLayout(<Component {...pageProps} />)}
                </GoogleOAuthProvider>
              </Hydrate>
              <ReactQueryDevtools initialIsOpen={false} />
            </PersistQueryClientProvider>
          </div>
        </ErrorBoundary>
      </main>
    </>
  );
}

export default MyApp;
